import { HiLightningBolt } from "react-icons/hi";

import { NovuComponent } from "MVVM/Components/NovuComponent";
import { useUser } from "MVVM/Hooks/useUser";
import { isPathActive, superNavLinks } from "../links";
import { UserSearch } from "../../UserSearch";
import { UserMenu } from "../UserMenu";
import { useTrialCta } from "../UserMenu/useTrialCta";
import { TrialButton } from "../UserMenu/styledComponents";
import {
  BurgerButton,
  NavDivider,
  SearchSlot,
  SuperNavLink,
  SuperNavLinks,
  LogoLink,
  StyledHeaderLogo,
  StyledHiMenu,
  SuperNavEnd,
  SuperNavInner,
  SuperNavRight,
  SuperNavStrip,
} from "../styledComponents";

/**
 * The permanent top strip: brand, search and identity.
 *
 * Present in both auth states, which is what stops search and the account
 * controls from relocating the moment you sign in. Signing in swaps the auth
 * buttons for the avatar and adds the bell; nothing moves.
 */
export const SuperNav = ({
  isLoggedIn,
  pathname,
  standalone,
  onOpenMobile,
}: {
  isLoggedIn: boolean;
  pathname: string;
  standalone?: boolean;
  onOpenMobile: () => void;
}) => {
  const { allstarUser } = useUser();
  const user = allstarUser?.user;
  const links = superNavLinks(user?.profile?.username || user?.username);
  const trial = useTrialCta();

  return (
    <SuperNavStrip $standalone={standalone}>
      <SuperNavInner $standalone={standalone}>
        <BurgerButton onClick={onOpenMobile} aria-label="Open navigation">
          <StyledHiMenu />
        </BurgerButton>
        <LogoLink to="/" aria-label="Allstar home">
          <StyledHeaderLogo />
        </LogoLink>
        {/* Marketing pages sell rather than search, matching the old standalone
          header. */}
        {!standalone && (
          <SearchSlot>
            <UserSearch compact />
          </SearchSlot>
        )}
        <SuperNavEnd>
          {isLoggedIn && (
            <>
              <SuperNavLinks aria-label="Your shortcuts">
                {links.map((link) => {
                  const Icon = link.icon;
                  const active = isPathActive(link.to, pathname);

                  return (
                    <SuperNavLink
                      key={link.to}
                      to={link.to}
                      $active={active}
                      aria-current={active ? "page" : undefined}
                    >
                      <Icon aria-hidden="true" />
                      {link.label}
                    </SuperNavLink>
                  );
                })}
              </SuperNavLinks>
              {/* The row changes meaning here: destinations to the left of it,
                account controls to the right. Signed out there are no
                shortcuts, so there is nothing to divide. */}
              <NavDivider aria-hidden="true" />
            </>
          )}
          <SuperNavRight>
            {/* Ahead of the bell: it is the one thing here asking for a decision,
              and it belongs with the account block it upsells. */}
            {trial.eligible && (
              <TrialButton type="button" onClick={trial.startTrial}>
                <HiLightningBolt aria-hidden="true" />
                {trial.label}
              </TrialButton>
            )}
            {/* Nothing to notify a visitor about. */}
            {isLoggedIn && <NovuComponent />}
            {/* Renders the auth buttons signed out and the avatar signed in, so the
            slot itself never moves. */}
            <UserMenu />
          </SuperNavRight>
        </SuperNavEnd>
      </SuperNavInner>
    </SuperNavStrip>
  );
};
